import React, { useState } from 'react';
import { suppliersAPI } from '../../services/api';
import { ModernInput, ModernButton } from '../ui/ModernComponents';
import { Truck, User, Phone, MapPin, Save, X, Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';

const defaultForm = {
  name: '',
  contact: '',
  phones: [{ phone: '', label: 'Principal' }],
  address: '',
};

function AddSupplierForm({ onSuccess, onCancel }) {
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handlePhoneChange = (index, field, value) => {
    const newPhones = [...form.phones];
    newPhones[index] = { ...newPhones[index], [field]: value };
    setForm(f => ({ ...f, phones: newPhones }));
  };

  const addPhone = () => {
    setForm(f => ({
      ...f,
      phones: [...f.phones, { phone: '', label: 'Principal' }]
    }));
  };

  const removePhone = (index) => {
    if (form.phones.length > 1) {
      const newPhones = form.phones.filter((_, i) => i !== index);
      setForm(f => ({ ...f, phones: newPhones }));
    }
  };

  const setPrimaryPhone = (index) => {
    const newPhones = form.phones.map((phone, i) => ({
      ...phone,
      label: i === index ? 'Principal' : phone.label === 'Principal' ? 'Secondaire' : phone.label
    }));
    setForm(f => ({ ...f, phones: newPhones }));
  };

  const handleSubmit = async () => {
    // Validate that at least one phone number is provided
    const validPhones = form.phones.filter(p => p.phone.trim());
    if (validPhones.length === 0) {
      toast.error('Au moins un numéro de téléphone est requis');
      return;
    }

    // Validate that all phone numbers are unique
    const phoneNumbers = validPhones.map(p => p.phone);
    const uniquePhones = new Set(phoneNumbers);
    if (phoneNumbers.length !== uniquePhones.size) {
      toast.error('Les numéros de téléphone doivent être uniques');
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        ...form,
        phones: validPhones
      };
      
      const res = await suppliersAPI.createSupplier(submitData);
      toast.success('Fournisseur ajouté');
      setForm(defaultForm);
      if (onSuccess) onSuccess(res.data);
    } catch (err) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div>
          <label className="block text-base font-semibold text-gray-800 mb-1">Nom du fournisseur</label>
          <ModernInput
            name="name"
            value={form.name}
            onChange={handleChange}
            placeholder="Ex: Optique Plus"
            icon={<Truck className="w-5 h-5" />}
            required
            className="w-full"
          />
        </div>
        
        <div>
          <label className="block text-base font-semibold text-gray-800 mb-1">Contact</label>
          <ModernInput
            name="contact"
            value={form.contact}
            onChange={handleChange}
            placeholder="Ex: Jean Dupont"
            icon={<User className="w-5 h-5" />}
            className="w-full"
          />
        </div>

        <div>
          <label className="block text-base font-semibold text-gray-800 mb-1">
            Numéros de téléphone
            <span className="text-red-500 ml-1">*</span>
          </label>
          <div className="space-y-3">
            {form.phones.map((phone, index) => (
              <div key={index} className="flex items-center space-x-3">
                <div className="flex-1">
                  <ModernInput
                    value={phone.phone}
                    onChange={(e) => handlePhoneChange(index, 'phone', e.target.value)}
                    placeholder="Ex: +33 1 23 45 67 89"
                    icon={<Phone className="w-5 h-5" />}
                    className="w-full"
                  />
                </div>
                <div className="w-32">
                  <ModernInput
                    value={phone.label}
                    onChange={(e) => handlePhoneChange(index, 'label', e.target.value)}
                    placeholder="Label"
                    className="w-full"
                  />
                </div>
                <div className="flex items-center space-x-1">
                  <ModernButton
                    type="button"
                    variant={phone.label === 'Principal' ? 'primary' : 'secondary'}
                    size="sm"
                    onClick={() => setPrimaryPhone(index)}
                    className="px-2 py-1 text-xs"
                  >
                    Principal
                  </ModernButton>
                  {form.phones.length > 1 && (
                    <ModernButton
                      type="button"
                      variant="danger"
                      size="sm"
                      onClick={() => removePhone(index)}
                      icon={<Trash2 className="w-4 h-4" />}
                      className="px-2 py-1"
                    />
                  )}
                </div>
              </div>
            ))}
            <ModernButton
              type="button"
              variant="secondary"
              onClick={addPhone}
              icon={<Plus className="w-4 h-4" />}
              className="w-full"
            >
              Ajouter un numéro de téléphone
            </ModernButton>
          </div>
        </div>

        <div>
          <label className="block text-base font-semibold text-gray-800 mb-1">Adresse</label>
          <ModernInput
            name="address"
            value={form.address}
            onChange={handleChange}
            placeholder="Ex: 123 Rue de la Paix, Paris"
            icon={<MapPin className="w-5 h-5" />}
            className="w-full"
          />
        </div>
      </div>
      
      <div className="flex justify-end space-x-2 pt-2">
        <ModernButton type="button" variant="secondary" onClick={onCancel} icon={<X className="w-4 h-4" />}>
          Annuler
        </ModernButton>
        <ModernButton type="button" variant="primary" icon={<Save className="w-4 h-4" />} disabled={loading} onClick={handleSubmit}>
          {loading ? 'Ajout...' : 'Ajouter'}
        </ModernButton>
      </div>
    </div>
  );
}

export default AddSupplierForm; 